/**
 * Eviction/arrears notices — auto-served by the Node backend's
 * checkOverdueInvoices cron job when a RENT invoice goes 7 days overdue (see
 * refactor/backend/src/jobs/checkOverdueInvoices.ts). Node-only feature, no
 * Django counterpart — see refactor/docs/PARITY_NOTES.md.
 *
 * Mounted identically for all three role trees (router.tsx) —
 * `evictionNoticeScope` (rbac.ts) deliberately grants TENANT read access to
 * notices served against their OWN lease, unlike most manager-only Node
 * modules, so a tenant can see why. The withdraw action is hidden entirely
 * for non-manager roles rather than left visible-but-403, same convention
 * InvoiceListPage uses for its own write affordances.
 *
 * There is no create form: a notice is never created through this API, only
 * auto-served by the cron job above.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlertTriangle } from 'lucide-react';
import { useAuth } from '../../auth/AuthContext';
import { parseApiError } from '../../lib/apiError';
import { useIsMobile } from '../../lib/useIsMobile';
import { EmptyState } from '../../components/EmptyState';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { listEvictionNotices, withdrawEvictionNotice } from '../../api/endpoints/evictionNotices';
import type { EvictionNotice } from '../../types/evictionNotice';

const STATUS_TONE: Record<EvictionNotice['status'], string> = {
  SERVED: '#dc2626',
  WITHDRAWN: '#8b826b',
  ACTED_ON: '#059669',
};

/**
 * A one-page formal notice letter, not a tabular export — built by hand with
 * jsPDF text calls (no `drawTable`) since a notice has no line items, only a
 * `lib/pdf.ts` branded header for visual consistency with the other 4 PDF
 * exports. Dynamically imported for the same reason InvoiceDetailPage's
 * handlePrintInvoice is: avoid bundling html2canvas for every page.
 */
async function downloadNoticePdf(notice: EvictionNotice): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const { createBrandedDocument, downloadPdf, formatPdfDate, PAGE_MARGIN } = await import('../../lib/pdf');

  const { doc, contentStartY } = createBrandedDocument(jsPDF, 'NOTICE OF RENT ARREARS');
  let y = contentStartY;
  const width = doc.internal.pageSize.getWidth();

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(`Property: ${notice.property_name}`, PAGE_MARGIN, y);
  y += 6;
  doc.text(`Lease: ${notice.lease_display}`, PAGE_MARGIN, y);
  y += 6;
  doc.text(`Notice served: ${formatPdfDate(notice.served_at)}`, PAGE_MARGIN, y);
  y += 6;
  doc.text(`Notice period: ${notice.notice_period_days} days`, PAGE_MARGIN, y);
  y += 6;
  doc.text(`Expires: ${formatPdfDate(notice.expires_at)}`, PAGE_MARGIN, y);
  y += 12;

  doc.setFont('helvetica', 'bold');
  doc.text('Reason:', PAGE_MARGIN, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  const reasonLines = doc.splitTextToSize(notice.reason || 'Rent arrears.', width - PAGE_MARGIN * 2) as string[];
  doc.text(reasonLines, PAGE_MARGIN, y);
  y += reasonLines.length * 5 + 10;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  doc.setTextColor(STATUS_TONE[notice.status]);
  doc.text(`Status: ${notice.status}`, PAGE_MARGIN, y);
  doc.setTextColor('#000000');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  y += 12;

  const noticeText = [
    'This notice is served in connection with unpaid rent under the above lease.',
    'Please contact your landlord or managing agent immediately to settle the',
    'outstanding balance and avoid further action, including potential',
    'termination of your lease upon expiry of the notice period stated above.',
  ];
  doc.text(noticeText, PAGE_MARGIN, y);

  downloadPdf(doc, `eviction-notice-${notice.id}.pdf`);
}

export function EvictionNoticeListPage(): React.JSX.Element {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const isManager = user?.role === 'LANDLORD' || user?.role === 'AGENT' || user?.role === 'SUPERAGENT';

  const [page, setPage] = useState(1);
  const [confirmWithdrawId, setConfirmWithdrawId] = useState<number | null>(null);

  const query = useQuery({
    queryKey: ['eviction-notices', { page }],
    queryFn: () => listEvictionNotices({ page }),
  });

  const withdraw = useMutation({
    mutationFn: (id: number) => withdrawEvictionNotice(id),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['eviction-notices'] }),
  });

  const notices = query.data?.results ?? [];
  const hasNext = query.data?.next !== null && query.data?.next !== undefined;
  const hasPrevious = query.data?.previous !== null && query.data?.previous !== undefined;

  function renderActions(notice: EvictionNotice): React.JSX.Element {
    return (
      <span className="admin-controls">
        <button type="button" onClick={() => void downloadNoticePdf(notice)}>
          Download notice
        </button>{' '}
        {isManager && notice.status === 'SERVED' && (
          <button
            type="button"
            disabled={withdraw.isPending && withdraw.variables === notice.id}
            onClick={() => setConfirmWithdrawId(notice.id)}
          >
            Withdraw
          </button>
        )}
      </span>
    );
  }

  return (
    <section>
      <h2>Eviction / arrears notices</h2>
      <p>
        Notices are served automatically when a rent invoice goes overdue by more than the
        notice period — they are never created manually here.
      </p>

      {query.isPending && <p>Loading…</p>}
      {query.isError && (
        <p role="alert" className="form__error">
          {parseApiError(query.error).message}
        </p>
      )}
      {query.isSuccess && notices.length === 0 && (
        <EmptyState icon={AlertTriangle} message="No eviction notices." />
      )}

      {query.isSuccess &&
        notices.length > 0 &&
        (isMobile ? (
          <ul className="record-cards">
            {notices.map((notice) => (
              <li key={notice.id} className="record-card">
                <div className="record-card__row">
                  <span className="record-card__label">Lease</span>
                  <span className="record-card__value">{notice.lease_display}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Property</span>
                  <span className="record-card__value">{notice.property_name}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Served</span>
                  <span className="record-card__value">{new Date(notice.served_at).toLocaleDateString()}</span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Expires</span>
                  <span className="record-card__value">
                    {new Date(notice.expires_at).toLocaleDateString()}
                    {notice.is_expired && notice.status === 'SERVED' ? ' (expired)' : ''}
                  </span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Status</span>
                  <span className="record-card__value" style={{ color: STATUS_TONE[notice.status] }}>
                    {notice.status}
                  </span>
                </div>
                <div className="record-card__row">
                  <span className="record-card__label">Actions</span>
                  <span className="record-card__value">{renderActions(notice)}</span>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Lease</th>
                <th>Property</th>
                <th>Served</th>
                <th>Expires</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {notices.map((notice) => (
                <tr key={notice.id}>
                  <td>{notice.lease_display}</td>
                  <td>{notice.property_name}</td>
                  <td>{new Date(notice.served_at).toLocaleDateString()}</td>
                  <td>
                    {new Date(notice.expires_at).toLocaleDateString()}
                    {notice.is_expired && notice.status === 'SERVED' ? ' (expired)' : ''}
                  </td>
                  <td style={{ color: STATUS_TONE[notice.status] }}>{notice.status}</td>
                  <td>{renderActions(notice)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ))}

      <p>
        <button type="button" disabled={!hasPrevious} onClick={() => setPage((p) => p - 1)}>
          Previous
        </button>{' '}
        <span>Page {page}</span>{' '}
        <button type="button" disabled={!hasNext} onClick={() => setPage((p) => p + 1)}>
          Next
        </button>
      </p>

      <ConfirmDialog
        open={confirmWithdrawId !== null}
        title="Withdraw eviction notice"
        message="Withdraw this notice? The lease will no longer be blocked from terminating, and this notice can be re-served automatically if the same rent invoice is still overdue."
        tone="danger"
        confirmLabel="Withdraw"
        onConfirm={() => {
          if (confirmWithdrawId !== null) {
            withdraw.mutate(confirmWithdrawId);
          }
          setConfirmWithdrawId(null);
        }}
        onCancel={() => setConfirmWithdrawId(null)}
      />
    </section>
  );
}

export default EvictionNoticeListPage;
